<template>
  <div>
    <div class="flex items-center gap-3 max-w-xs">
      <div
        class="flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
        :class="
          isSent
            ? 'bg-white/25 dark:bg-[var(--p-primary-100)]/15'
            : 'bg-[var(--p-primary-color)]/10 dark:bg-[var(--p-primary-100)]/10'
        "
      >
        <component
          :is="getFileIcon(fileName)"
          :size="20"
          :class="
            isSent
              ? 'text-white dark:text-[var(--p-primary-50)]'
              : 'text-[var(--p-primary-color)] dark:text-[var(--p-primary-100)]'
          "
        />
      </div>

      <div class="flex-1 min-w-0">
        <button
          type="button"
          class="text-sm font-normal truncate mb-1 block w-full text-left"
          :class="[
            url ? 'cursor-pointer hover:underline' : 'cursor-default',
            isSent
              ? 'text-[var(--p-surface-50)] dark:text-[var(--p-primary-50)]'
              : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]',
          ]"
          @click="openFile"
        >
          {{ fileName || 'Documento' }}
        </button>

        <p
          class="text-xs"
          :class="
            isSent
              ? 'text-[var(--p-primary-50)]/70 dark:text-[var(--p-primary-100)]/70'
              : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
          "
        >
          {{ fileSize ? formatFileSize(fileSize) : fileName ? getFileExtension(fileName) : '' }}
        </p>
      </div>

      <button
        v-if="url"
        type="button"
        class="flex-shrink-0 cursor-pointer w-9 h-9 rounded-full flex items-center justify-center transition-all"
        :class="
          isSent
            ? 'text-white/80 hover:bg-white/20 hover:text-white dark:text-[var(--p-primary-100)]/80 dark:hover:bg-[var(--p-primary-100)]/15 dark:hover:text-[var(--p-primary-50)]'
            : 'hover:bg-neutral-800/10 dark:hover:bg-neutral-200/10'
        "
        @click="downloadFile"
      >
        <Download
          :size="18"
          :class="
            isSent
              ? 'text-current'
              : 'text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]'
          "
        />
      </button>
    </div>

    <div
      v-if="caption"
      class="mt-2 text-sm"
      :class="
        isSent
          ? 'text-[var(--p-surface-0)] dark:text-[var(--p-primary-50)]'
          : 'text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]'
      "
    >
      <FormattedText :text="caption" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Download,
  File,
  FileText,
  FileSpreadsheet,
  FileArchive,
  FileCode,
  FileType,
  FileImage,
  FileVideo,
  FileAudio,
} from 'lucide-vue-next'
import FormattedText from '../../atoms/text/FormattedText.vue'
import { useFileFormatter } from '../../composables/useFileFormatter'

interface Props {
  url?: string
  fileName?: string
  fileSize?: string | number
  caption?: string
  isSent: boolean
}

const props = defineProps<Props>()

const { formatFileSize, getFileExtension, getFileIconName } = useFileFormatter()

const getFileIcon = (fileName: string | null | undefined) => {
  const iconName = getFileIconName(fileName)

  const iconMap: Record<string, typeof File> = {
    File,
    FileText,
    FileSpreadsheet,
    FileArchive,
    FileCode,
    FileType,
    FileImage,
    FileVideo,
    FileAudio,
  }

  return iconMap[iconName] || File
}

const openFile = () => {
  if (!props.url) return

  window.open(props.url, '_blank', 'noopener,noreferrer')
}

const downloadFile = async () => {
  if (!props.url) return

  const fileName = props.fileName || 'documento'

  try {
    const response = await fetch(props.url)
    const blob = await response.blob()

    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  } catch {
    const link = document.createElement('a')
    link.href = props.url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
</script>
